import { DetailSectionTemplate } from "components/MainSectionTemplate/DetailSectionTemplate";
import { SectionTemplateBrField } from "components/MainSectionTemplate/SectionTemplateBrField";
import SectionPanel from "components/panels/SectionPanel/SectionPanel";
import { BankAccountSmallDetails, BookkeepingSmallDetails, CompanySmallDetails } from "components/SmallDetails";
import SmallDetails from "components/SmallDetails/SmallDetails";
import LifecycleSteps, { LifecycleStepsType, StepOptions } from "components/Steps/LifecycleSteps";
import React from "react";
import { formatDaysSince, formatWrittenDayMonthYear, getCurrency } from "utilities";
import ViewInvoiceButtonPart from "../ViewInvoiceButtonPart";
import { CurrencyAndExchangeRate } from "components/exchangerate/CurrencyAndExchangeRatePicker";
import UserWithAvatar from "components/Atoms/UserWithAvatar";
import { CostCenterLabel } from "components/Removables/RemovableCostCenter";
import { DocumentSendDate } from "components/Document/DocumentSendDate";
import moment from "moment";
import { isInvoiceOverdue } from "utilities/invoiceUtils";
import { DocumentDueDate } from "components/Document/DocumentDueDate";
import { PeppolAfterInvoiceSent } from "../Peppol/PeppolAfterInvoiceSent";
import { countrySupportsPeppol } from "utilities/eInvoiceUtils";
import { PeppolBeforeInvoiceSent } from "../Peppol/PeppolBeforeInvoiceSent";

interface InvoiceV2InfoSectionProps {
  stepOptions: StepOptions
  activeStep: LifecycleStepsType
  invoice: server.dto.InvoiceV2;
  bookkeeping: server.dto.Bookkeeping;
  productsByParent: server.dto.ProductsByParent;
  invoiceSent: server.dto.InvoiceSent;
}

export const InvoiceV2InfoSection = (props: InvoiceV2InfoSectionProps) => {
  const hasOnlyOneBrand = !site.currentEnvironment.hasMultipleBrands

  const displayInReviewSince = props.invoice.lifeCycle === "review";
  const displayOutstandingSince = props.invoice.lifeCycle === "pending";
  const displaySendDate = props.invoice.lifeCycle === "completed";
  const displayDueDate = props.invoice.lifeCycle === "review" ||
    props.invoice.lifeCycle === "pending";
  const displayOgm = props.bookkeeping.companyAdmCountry === "belgium" && props.invoice.lifeCycle !== "review";

  const isOverdue = isInvoiceOverdue(props.invoice.invoiceDate, props.invoice.dueInDays, props.invoice.lifeCycle);

  return <DetailSectionTemplate
    isInPageEditable={false}
    onValueChanged={() => { }}
    sections={[{
      key: "invoiceDetails",
      type: "regular",
      title: RESX.InvoiceDocument.Invoice,
      // supportArticleId: serverReference.supportArticleIds.whatIsInvoiceing,
      fields: [{
        key: "lifecycleSteps",
        label: "",
        hideLabel: true,
        compact: true,
        render: () => <LifecycleSteps
          documentType="invoice"
          pendingLabel={RESX.Financial.Outstanding}
          completedLabel={RESX.Financial.Settled}
          stepOptions={props.stepOptions}
          overDue={isOverdue}
          activeStep={props.activeStep}
          documentId={props.invoice.invoiceId}
        />
      }, SectionTemplateBrField("br"),
      {
        key: "brand",
        label: RESX.Branding.resxBranding,
        hidden: hasOnlyOneBrand,
        render: () => <div>{props.invoice.brand?.name}</div>,
      }, {
        key: "documentNumber",
        label: RESX.InvoiceDocument.InvoiceNumber,
        hidden: props.invoice.lifeCycle === "draft" || props.invoice.lifeCycle === "review",
        render: () => <div>{props.invoice.documentNumber}</div>
      }, {
        key: "belgianOgmNumber",
        label: RESX.Financial.OgmCode,
        hidden: !displayOgm,
        render: () => <div>{props.invoice.belgianOgmCode}</div>
      }, {
        key: "bookkeeping",
        label: RESX.Bookkeeping.resxBookkeeping,
        render: () => <BookkeepingSmallDetails
          bookkeeping={props.invoice.bookkeeping}
        />
      }, {
        key: "currencyExchangeRate",
        label: RESX.Financial.Currency,
        render: () => <CurrencyAndExchangeRate
          bookkeeping={props.invoice.bookkeeping}
          currency={getCurrency(props.invoice.currency)}
          exchangeRate={props.invoice.exchangeRate}
        />
      }, {
        key: "bankAccount",
        label: RESX.BankAccount.resxBankAccount,
        render: () => <BankAccountSmallDetails
          bankAccount={props.invoice.bankAccount}
        />
      }, {
        key: "company",
        label: RESX.Company.resxCompany,
        render: () => <CompanySmallDetails company={props.invoice.company} layout="multiLine" />,
      }, {
        key: "peppol",
        label: "Peppol",
        hidden: !countrySupportsPeppol(props.invoice.bookkeeping.companyAdmCountry),
        render: () => props.invoice.lifeCycle === "review"
          ? <PeppolBeforeInvoiceSent invoiceId={props.invoice.invoiceId} addresseeCompanyId={props.invoice.company.companyId} />
          : <PeppolAfterInvoiceSent invoiceId={props.invoice.invoiceId} addresseeCompanyId={props.invoice.company.companyId} invoiceSent={props.invoiceSent} />
      }, {
        key: "outstandingSince",
        label: RESX.DateTime.SendDate,
        hidden: !displayOutstandingSince,
        render: () => <SmallDetails
          label={formatWrittenDayMonthYear(props.invoice.invoiceDate)}
          subLabel={formatDaysSince(props.invoice.invoiceDate)}
        />
      }, {
        key: "sendDate",
        label: RESX.DateTime.SendDate,
        hidden: !displaySendDate,
        render: () => <DocumentSendDate
          sendDate={moment.utc(props.invoice.invoiceDate)}
          lifeCycle={props.invoice.lifeCycle}
          hasProducts={props.productsByParent.collection.length > 0}
        />
      }, {
        key: "dueDate",
        label: RESX.DateTime.Due,
        hidden: !displayDueDate,
        render: () => <DocumentDueDate
          dueDate={moment.utc(props.invoice.invoiceDate)}
          dueInDays={props.invoice.dueInDays}
          lifeCycle={props.invoice.lifeCycle}
          hasProducts={props.productsByParent.collection.length > 0}
        />
      }, {
        key: "inReviewSince",
        label: RESX.DocumentLifeCycle.InReview,
        hidden: !displayInReviewSince,
        render: () => <SmallDetails
          label={formatWrittenDayMonthYear(props.invoice.invoiceDate)}
          subLabel={formatDaysSince(props.invoice.invoiceDate)}
        />
      }, {
        key: "costCenter",
        label: RESX.CostCenter.resxCostCenter,
        hidden: !site.currentEnvironment.hasCostCenters,
        render: () => <CostCenterLabel value={props.invoice.costCenter} />,
      }, {
        key: "projectCode",
        label: RESX.Show.ProjectCode,
        isEmpty: !props.invoice.projectCode,
        render: () => <div>{props.invoice.projectCode}</div>,
        hidden: site.currentEnvironment.projectCode === "hidden",
      }, {
        key: "purchaseOrderNumber",
        label: RESX.InvoiceDocument.PurchaseOrderNumber,
        isEmpty: !props.invoice.purchaseOrderNumber,
        hidden: !site.currentEnvironment.purchaseOrderNumber,
        render: () => <div>{props.invoice.purchaseOrderNumber}</div>,
      }, {
        key: "owner",
        label: RESX.GeneralLabels.Owner,
        render: () => <UserWithAvatar user={props.invoice.owner} />,
      }]
    }, {
      key: "previewDocument",
      type: "custom",
      render: () => <SectionPanel.Panel
        key="previewDocument"
        id="previewDocument"
      >
        <ViewInvoiceButtonPart
          documentNumber={props.invoice.documentNumber}
          label={RESX.InvoiceDocument.ViewInvoice}
          documentId={props.invoice.invoiceId}
        />
      </SectionPanel.Panel>
    }]}
  />
}